/**
 * Configuración del módulo HR Documents:
 *   - Tipos de documento (Contrato, Anexo, Liquidación, ...)
 *   - Plantillas de carpetas (estructura estándar replicable a empleados)
 */
import { useState } from 'react'
import { Plus, Pencil, Trash2, FileText, Folder } from 'lucide-react'

import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Modal } from '@/components/ds/Modal'
import { DataTable } from '@/components/ds/DataTable'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { useToast, ToastContainer } from '@/components/Toast'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { Badge } from '@/components/Badge'
import { inputCls, labelCls } from '@/lib/formStyles'
import { extractFieldErrors, extractErrorMessage } from '@/lib/errorUtils'
import { TextField } from '@/components/ds/TextField'

import {
  useDocumentTypes,
  useCreateDocumentType,
  useUpdateDocumentType,
  useDeleteDocumentType,
  useFolderTemplates,
  useCreateFolderTemplate,
  useUpdateFolderTemplate,
  useDeleteFolderTemplate,
  type DocumentType,
  type FolderTemplate,
} from '@/modules/payroll/hooks/useHrDocuments'


type SubTab = 'types' | 'templates'

export function HrDocumentSettingsTab() {
  const [subTab, setSubTab] = useState<SubTab>('types')
  const toast = useToast()

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
      <div className="flex gap-2 border-b border-divider">
        <TabButton active={subTab === 'types'} onClick={() => setSubTab('types')} icon={FileText}>
          Tipos de documento
        </TabButton>
        <TabButton active={subTab === 'templates'} onClick={() => setSubTab('templates')} icon={Folder}>
          Plantillas de carpetas
        </TabButton>
      </div>

      {subTab === 'types' && <DocumentTypesPanel toast={toast} />}
      {subTab === 'templates' && <FolderTemplatesPanel toast={toast} />}
    </div>
  )
}


function TabButton({
  active, onClick, icon: Icon, children,
}: {
  active: boolean
  onClick: () => void
  icon: typeof FileText
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={
        'flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ' +
        (active
          ? 'border-primary text-primary'
          : 'border-transparent text-content-muted hover:text-content')
      }
    >
      <Icon size={14} aria-hidden="true" />
      {children}
    </button>
  )
}


type ToastApi = ReturnType<typeof useToast>


// ─── Document Types ──────────────────────────────────────────────────────────

function DocumentTypesPanel({ toast }: { toast: ToastApi }) {
  const { data: types, isLoading } = useDocumentTypes()
  const { data: templates } = useFolderTemplates()
  const [editing, setEditing] = useState<DocumentType | 'new' | null>(null)
  const del = useDeleteDocumentType()
  const { confirm, modalProps } = useConfirmModal()

  const handleDelete = (t: DocumentType) =>
    confirm({
      message: `¿Eliminar el tipo de documento "${t.name}"?`,
      variant: 'danger',
      confirmLabel: 'Eliminar',
      onConfirm: async () => {
        try {
          await del.mutateAsync(t.id)
          toast.success('Tipo eliminado')
        } catch (e) {
          toast.error(extractErrorMessage(e, 'Error al eliminar'))
        }
      },
    })

  return (
    <div className="space-y-3">
      <ConfirmModal {...modalProps} />
      <div className="flex justify-between items-center">
        <p className="text-sm text-content-muted">
          Categorías para clasificar los documentos del trabajador (Contrato, Anexo, Certificado, ...).
        </p>
        <Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Nuevo tipo</Button>
      </div>

      <DataTable
        rows={types ?? []}
        rowKey={t => t.id}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={4} cols={4} />}
        minWidth={720}
        caption="Tipos de documento del personal"
        empty={
          <EmptyState
            icon={FileText}
            title="Aún no hay tipos de documento"
            hint="Crear el primero (ej. Contrato, Anexo, Certificado médico) para clasificar los documentos del personal."
            action={<Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Crear primer tipo</Button>}
          />
        }
        columns={[
          { key: 'name', header: 'Nombre', sortable: true },
          {
            key: 'code', header: 'Código', sortable: true, width: 140,
            render: t => <span className="font-mono text-xs text-content-muted">{t.code}</span>,
          },
          {
            key: 'default_folder_name', header: 'Carpeta default', sortable: true,
            render: t => (
              <span className="text-content-muted">
                {t.default_folder_name || <span className="text-content-subtle">—</span>}
              </span>
            ),
          },
          {
            key: 'requires_signature', header: 'Firma', sortable: true, width: 110,
            // Ordena por si EXIGE firma y de qué tipo, no por el rótulo: lo que
            // interesa es juntar los que la piden.
            sortValue: t => (t.requires_signature ? (t.signature_type ?? 'simple') : null),
            render: t => t.requires_signature ? (
              <Badge label={t.signature_type === 'advanced' ? 'Avanzada' : 'Simple'} variant="blue" />
            ) : (
              <span className="text-content-subtle">—</span>
            ),
          },
          {
            key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 96,
            render: t => (
              <div className="text-right whitespace-nowrap">
                <IconButton
                  icon={<Pencil size={14} aria-hidden="true" />}
                  aria-label="Editar"
                  onClick={() => setEditing(t)}
                  size="sm"
                />
                <IconButton
                  icon={<Trash2 size={14} aria-hidden="true" />}
                  aria-label="Eliminar"
                  variant="danger"
                  onClick={() => handleDelete(t)}
                  size="sm"
                />
              </div>
            ),
          },
        ]}
      />

      {editing && (
        <DocumentTypeFormModal
          initial={editing === 'new' ? null : editing}
          templates={templates ?? []}
          onClose={() => setEditing(null)}
          toast={toast}
        />
      )}
    </div>
  )
}


function DocumentTypeFormModal({
  initial, templates, onClose, toast,
}: {
  initial: DocumentType | null
  templates: FolderTemplate[]
  onClose: () => void
  toast: ToastApi
}) {
  const create = useCreateDocumentType()
  const update = useUpdateDocumentType()
  const isEdit = initial !== null

  const [form, setForm] = useState({
    name: initial?.name ?? '',
    code: initial?.code ?? '',
    description: initial?.description ?? '',
    requires_signature: initial?.requires_signature ?? false,
    signature_type: initial?.signature_type ?? 'simple',
    default_folder: initial?.default_folder ?? '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      description: form.description.trim(),
      requires_signature: form.requires_signature,
      // 🔴 Siempre 'simple', y no es un descuido: la firma AVANZADA (Ley 19.799)
      // exige un certificado por persona de un prestador acreditado, y no hay
      // driver (FirmaVirtual / eCertChile / DocuSign). Ofrecerla haría que el
      // sistema firmara simple y lo rotulara avanzada, que es afirmar algo falso
      // sobre el valor probatorio de un documento laboral.
      signature_type: 'simple' as const,
      default_folder: form.default_folder || null,
    }
    try {
      if (isEdit && initial) {
        await update.mutateAsync({ id: initial.id, ...payload })
        toast.success('Tipo actualizado')
      } else {
        await create.mutateAsync(payload)
        toast.success('Tipo creado')
      }
      onClose()
    } catch (err) {
      const fieldErrors = extractFieldErrors(err)
      if (fieldErrors) setErrors(fieldErrors)
      else toast.error(extractErrorMessage(err, 'Error al guardar'))
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar tipo de documento' : 'Nuevo tipo de documento'} size="sm">
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label htmlFor="hrdocumentsettingstab-name-2" className={labelCls}>Nombre</label>
          <input id="hrdocumentsettingstab-name-2"
            className={inputCls}
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="ej. Contrato de trabajo"
            required
          />
          {errors.name && <p className="text-xs text-danger mt-1">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="hrdocumentsettingstab-code" className={labelCls}>Código (interno)</label>
          <input id="hrdocumentsettingstab-code"
            className={inputCls}
            value={form.code}
            onChange={e => setForm({ ...form, code: e.target.value })}
            placeholder="ej. contrato_trabajo"
            required
          />
          {errors.code && <p className="text-xs text-danger mt-1">{errors.code}</p>}
        </div>

        <div>
          <label htmlFor="hrdocumentsettingstab-default-folder" className={labelCls}>Carpeta default (opcional)</label>
          <select id="hrdocumentsettingstab-default-folder"
            className={inputCls}
            value={form.default_folder}
            onChange={e => setForm({ ...form, default_folder: e.target.value })}
          >
            <option value="">— Sin carpeta predeterminada —</option>
            {templates.map(tpl => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
          <p className="text-xs text-content-muted mt-1">
            Carpeta en la que se sube por defecto este tipo de documento.
          </p>
        </div>

        <div className="border-t border-divider pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.requires_signature}
              onChange={e => setForm({ ...form, requires_signature: e.target.checked })}
            />
            <span className="text-sm">Requiere firma electrónica</span>
          </label>
          <p className="text-xs text-content-muted mt-1">
            Al subir un documento de este tipo, se le pide la firma al trabajador y le llega un
            aviso por correo. Firma electrónica <strong>simple</strong> (Ley 19.799), con la
            identidad confirmada por un código de un solo uso.
            {/* ⚠️ Decirlo acá y no solo en la constancia: es donde RRHH decide qué
                documentos van por esta vía, y la avanzada requiere otra cosa. */}
          </p>
        </div>

        <TextField
          dense
          label="Descripción (opcional)"
          multiline
          rows={2}
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          placeholder="Notas para el equipo..."
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
            pendingLabel={isEdit ? 'Guardando...' : 'Creando...'}
          >
            {isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}


// ─── Folder Templates ────────────────────────────────────────────────────────

function FolderTemplatesPanel({ toast }: { toast: ToastApi }) {
  const { data: templates, isLoading } = useFolderTemplates()
  const [editing, setEditing] = useState<FolderTemplate | 'new' | null>(null)
  const del = useDeleteFolderTemplate()
  const { confirm, modalProps } = useConfirmModal()

  const handleDelete = (t: FolderTemplate) =>
    confirm({
      message: `¿Eliminar la plantilla "${t.name}"?`,
      variant: 'danger',
      confirmLabel: 'Eliminar',
      onConfirm: async () => {
        try {
          await del.mutateAsync(t.id)
          toast.success('Plantilla eliminada')
        } catch (e) {
          toast.error(extractErrorMessage(e, 'Error al eliminar'))
        }
      },
    })

  return (
    <div className="space-y-3">
      <ConfirmModal {...modalProps} />
      <div className="flex justify-between items-center">
        <p className="text-sm text-content-muted">
          Catálogo de carpetas estándar de la empresa (Contratos, Liquidaciones, Certificados...).
        </p>
        <Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Nueva plantilla</Button>
      </div>

      <DataTable
        rows={templates ?? []}
        rowKey={t => t.id}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={4} cols={3} />}
        minWidth={560}
        caption="Plantillas de carpeta de la empresa"
        empty={
          <EmptyState
            icon={Folder}
            title="Aún no hay plantillas de carpeta"
            hint="Crear las carpetas estándar de la empresa (ej. Contratos, Liquidaciones, Certificados)."
            action={<Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Crear primera plantilla</Button>}
          />
        }
        columns={[
          { key: 'name', header: 'Nombre', sortable: true },
          {
            key: 'order', header: 'Orden', numeric: true, sortable: true, width: 100,
            render: t => <span className="text-content-muted">{t.order}</span>,
          },
          {
            key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 96,
            render: t => (
              <div className="text-right whitespace-nowrap">
                <IconButton icon={<Pencil size={14} aria-hidden="true" />} aria-label="Editar" onClick={() => setEditing(t)} size="sm" />
                <IconButton icon={<Trash2 size={14} aria-hidden="true" />} aria-label="Eliminar" variant="danger" onClick={() => handleDelete(t)} size="sm" />
              </div>
            ),
          },
        ]}
      />

      {editing && (
        <FolderTemplateFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          toast={toast}
        />
      )}
    </div>
  )
}


function FolderTemplateFormModal({
  initial, onClose, toast,
}: {
  initial: FolderTemplate | null
  onClose: () => void
  toast: ToastApi
}) {
  const create = useCreateFolderTemplate()
  const update = useUpdateFolderTemplate()
  const isEdit = initial !== null

  const [form, setForm] = useState({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    order: initial?.order?.toString() ?? '0',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      order: Number(form.order) || 0,
    }
    try {
      if (isEdit && initial) {
        await update.mutateAsync({ id: initial.id, ...payload })
        toast.success('Plantilla actualizada')
      } else {
        await create.mutateAsync(payload)
        toast.success('Plantilla creada')
      }
      onClose()
    } catch (err) {
      const fieldErrors = extractFieldErrors(err)
      if (fieldErrors) setErrors(fieldErrors)
      else toast.error(extractErrorMessage(err, 'Error al guardar'))
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar plantilla de carpeta' : 'Nueva plantilla de carpeta'} size="sm">
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label htmlFor="hrdocumentsettingstab-name" className={labelCls}>Nombre</label>
          <input id="hrdocumentsettingstab-name"
            className={inputCls}
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="ej. Contratos"
            required
          />
          {errors.name && <p className="text-xs text-danger mt-1">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="hrdocumentsettingstab-order" className={labelCls}>Orden</label>
          <input id="hrdocumentsettingstab-order"
            type="number"
            className={inputCls}
            value={form.order}
            onChange={e => setForm({ ...form, order: e.target.value })}
            min="0"
          />
          <p className="text-xs text-content-muted mt-1">Posición en la lista (menor = primero).</p>
        </div>

        <TextField
          dense
          label="Descripción (opcional)"
          multiline
          rows={2}
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
            pendingLabel={isEdit ? 'Guardando...' : 'Creando...'}
          >
            {isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
